import { useId } from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import { DynamicFormRequiredType } from '../../dynamic-form/dynamic-form.enums';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import OdinController from '../../dynamic-form/odin-controller.component';
import { Field, FieldError, FieldLabel, FieldTitle, FieldValue } from '../../field/field';
import type { InputProps } from './input.component';
import Input from './input.component';

export interface HfInputProps<TFieldValues extends FieldValues = FieldValues>
  extends InputProps,
    Pick<DynamicFieldComponentProps<string>, 'label' | 'labelStyles' | 'required'> {
  control: Control<TFieldValues>;
}

export default function HfInput<TFieldValues extends FieldValues = FieldValues>({
  control,
  name = '',
  label,
  type,
  size,
  value,
  placeholder,
  readOnly,
  required,
  onChange,
  onChangeEvent,
  fieldValueConverter,
  onFocus,
  disabled,
  debounceTime,
  validations,
  labelStyles,
  inputStyles,
  trimWhitespace = true,
  min,
  max,
  maxLength,
  enableDebounce,
  inputRef,
  autoFocus,
}: HfInputProps<TFieldValues>) {
  const id = useId();
  const errorId = `${id}-error`;
  const isRequired = required === DynamicFormRequiredType.True && !readOnly && !disabled;

  return (
    <OdinController
      name={name}
      defaultValue={value}
      control={control}
      onChange={onChange}
      fieldValueConverter={fieldValueConverter}
      render={(renderProps) => {
        const field = renderProps.field;
        const errorMessage = renderProps.fieldState.error?.message;

        if (readOnly) {
          const displayValue: unknown = field.value;
          const isDisplayable =
            (typeof displayValue === 'string' && displayValue !== '') || typeof displayValue === 'number';
          return (
            <Field>
              {label && <FieldTitle className={labelStyles}>{label}</FieldTitle>}
              <FieldValue isEmpty={!isDisplayable}>{isDisplayable ? displayValue : null}</FieldValue>
            </Field>
          );
        }

        return (
          <Field data-invalid={!!errorMessage} data-disabled={disabled}>
            {label && (
              <FieldLabel htmlFor={id} className={labelStyles} required={isRequired}>
                {label}
              </FieldLabel>
            )}
            <Input
              id={id}
              name={name}
              type={type}
              size={size}
              value={field.value}
              placeholder={placeholder}
              readOnly={readOnly}
              onChange={field.onChange}
              onChangeEvent={onChangeEvent}
              onFocus={onFocus}
              disabled={disabled}
              debounceTime={debounceTime}
              validations={validations}
              inputStyles={inputStyles}
              trimWhitespace={trimWhitespace}
              min={min}
              max={max}
              maxLength={maxLength}
              enableDebounce={enableDebounce}
              inputRef={inputRef}
              autoFocus={autoFocus}
              aria-invalid={!!errorMessage}
              aria-required={isRequired}
              aria-describedby={errorMessage ? errorId : undefined}
            />
            <FieldError id={errorId}>{errorMessage}</FieldError>
          </Field>
        );
      }}
    />
  );
}
